// DESCRIPTION: Step 1 of the event creation wizard — event name, rich-text
// DESCRIPTION: description, timezone, and category/tag assignment (with inline term
// DESCRIPTION: creation). Editorial two-column layout: content left, organize right.
import { __, sprintf } from '@wordpress/i18n';
import { useState } from '@wordpress/element';
import { TextControl, SelectControl } from '@wordpress/components';
import { decodeEntities } from '@wordpress/html-entities';
import FieldError from './FieldError';
import RichDescription from './RichDescription';

const CARD_DETAILS =
	'nb-rounded-xl nb-border nb-border-neutral-300 nb-bg-neutral-0 nb-p-6 nb-shadow-elevation-1';
const CARD_ORGANIZE =
	'nb-rounded-xl nb-border nb-border-neutral-300 nb-bg-neutral-50 nb-p-6 nb-shadow-elevation-1';

/**
 * A selectable taxonomy term rendered as a toggle pill.
 *
 * @since PBOOKING_SINCE
 *
 * @param {Object}   props
 * @param {Object}   props.term     Term object ({ id, name }).
 * @param {boolean}  props.selected Whether the term is currently selected.
 * @param {Function} props.onToggle Change handler receiving the checkbox event.
 *
 * @return {JSX.Element}
 */
const TermChip = ( { term, selected, onToggle } ) => (
	<label
		className={ `nb-flex nb-items-center nb-gap-1.5 nb-px-3 nb-py-1.5 nb-rounded-full nb-text-sm nb-cursor-pointer nb-border nb-transition-colors ${
			selected
				? 'nb-bg-accent-500 nb-border-accent-500 nb-text-white'
				: 'nb-bg-neutral-0 nb-border-neutral-300 nb-text-neutral-700 hover:nb-border-neutral-500'
		}` }
	>
		<input
			type="checkbox"
			className="nb-sr-only"
			style={ {
				position: 'absolute',
				opacity: 0,
				pointerEvents: 'none',
			} }
			checked={ selected }
			onChange={ onToggle }
		/>
		{ selected && (
			<span className="nb-text-xs" aria-hidden="true">
				&#x2713;
			</span>
		) }
		{ decodeEntities( term.name ) }
	</label>
);

/**
 * Inline "create a new term" input + Add button.
 *
 * @since PBOOKING_SINCE
 *
 * @param {Object}   props
 * @param {string}   props.placeholder Input placeholder text.
 * @param {Function} props.onCreate    Called with the trimmed term name.
 *
 * @return {JSX.Element}
 */
const TermCreator = ( { placeholder, onCreate } ) => {
	const [ draft, setDraft ] = useState( '' );

	const submit = () => {
		const value = draft.trim();
		if ( ! value ) {
			return;
		}
		onCreate( value );
		setDraft( '' );
	};

	return (
		<div className="nb-flex nb-gap-2 nb-mt-2.5">
			<input
				type="text"
				value={ draft }
				onChange={ ( e ) => setDraft( e.target.value ) }
				onKeyDown={ ( e ) => {
					if ( e.key === 'Enter' ) {
						e.preventDefault();
						submit();
					}
				} }
				placeholder={ placeholder }
				className="nb-flex-1 nb-h-9 nb-px-3 nb-box-border nb-rounded-md nb-border nb-border-neutral-300 nb-bg-neutral-0 nb-text-sm nb-text-neutral-900 focus:nb-border-accent-500"
			/>
			<button
				type="button"
				className="nemtly-button-secondary nemtly-button-sm"
				onClick={ submit }
				disabled={ ! draft.trim() }
			>
				<span aria-hidden="true">+</span>
				{ __( 'Add', 'nemtly-booking' ) }
			</button>
		</div>
	);
};

const BasicInfoStep = ( {
	formData,
	onFieldChange,
	fieldErrors = {},
	availableCategories = [],
	availableTags = [],
	onCreateCategory = () => {},
	onCreateTag = () => {},
} ) => {
	return (
		<div className="nemtly-wizard-step-content nb-grid nb-grid-cols-1 lg:nb-grid-cols-3 nb-gap-6 nb-items-start">
			{ /* Main column: content */ }
			<div className="lg:nb-col-span-2">
				<div className={ CARD_DETAILS }>
					<h3 className="nb-text-base nb-font-semibold nb-text-neutral-900 nb-mt-0 nb-mb-5">
						{ __( 'Details', 'nemtly-booking' ) }
					</h3>

					<div
						className={ `nb-mb-5 ${
							fieldErrors.title
								? 'nemtly-field-has-error'
								: ''
						}` }
					>
						<label className="nb-block nb-text-sm nb-font-semibold nb-text-neutral-700 nb-mb-1">
							{ __( 'Event Name', 'nemtly-booking' ) }{ ' ' }
							<span className="nb-text-danger-500">*</span>
						</label>
						<TextControl
							value={ formData.title }
							onChange={ ( value ) =>
								onFieldChange( 'title', value )
							}
							placeholder={ __(
								'Write your event Name',
								'nemtly-booking'
							) }
							__next40pxDefaultSize
							__nextHasNoMarginBottom
						/>
						<FieldError error={ fieldErrors.title } />
					</div>

					<div>
						<label className="nb-block nb-text-sm nb-font-semibold nb-text-neutral-700 nb-mb-1">
							{ __( 'Description', 'nemtly-booking' ) }
						</label>
						<RichDescription
							value={ formData.content }
							onChange={ ( value ) =>
								onFieldChange( 'content', value )
							}
						/>
					</div>
				</div>
			</div>

			{ /* Sidebar: organization */ }
			<div className={ CARD_ORGANIZE }>
				<h3 className="nb-text-base nb-font-semibold nb-text-neutral-900 nb-mt-0 nb-mb-5">
					{ __( 'Organize', 'nemtly-booking' ) }
				</h3>

				<div className="nb-mb-5">
					<label className="nb-block nb-text-sm nb-font-semibold nb-text-neutral-700 nb-mb-1">
						{ __( 'Timezone', 'nemtly-booking' ) }{ ' ' }
						<span className="nb-text-danger-500">*</span>
					</label>
					<SelectControl
						value={ formData.timezone }
						options={ ( window.nemtlyCreateEvent?.timezones || [] ).map(
							( tz ) => ( { label: tz, value: tz } )
						) }
						onChange={ ( value ) =>
							onFieldChange( 'timezone', value )
						}
						help={ __(
							'From WordPress Settings',
							'nemtly-booking'
						) }
						__next40pxDefaultSize
						__nextHasNoMarginBottom
					/>
				</div>

				<div className="nb-mb-5">
					<div className="nb-flex nb-items-baseline nb-justify-between nb-mb-2">
						<label className="nb-text-sm nb-font-semibold nb-text-neutral-700">
							{ __( 'Categories', 'nemtly-booking' ) }
						</label>
						<span className="nb-text-xs nb-text-neutral-500">
							{ sprintf(
								/* translators: %d: number of selected categories */
								__( '%d selected', 'nemtly-booking' ),
								formData.categories.length
							) }
						</span>
					</div>
					{ availableCategories.length > 0 && (
						<div className="nb-flex nb-flex-wrap nb-gap-2 nb-max-h-[7.5rem] nb-overflow-y-auto">
							{ availableCategories.map( ( cat ) => (
								<TermChip
									key={ cat.id }
									term={ cat }
									selected={ formData.categories.includes(
										cat.id
									) }
									onToggle={ ( e ) => {
										const updated = e.target.checked
											? [ ...formData.categories, cat.id ]
											: formData.categories.filter(
													( id ) => id !== cat.id
											  );
										onFieldChange( 'categories', updated );
									} }
								/>
							) ) }
						</div>
					) }
					<TermCreator
						placeholder={ __(
							'Create a new category…',
							'nemtly-booking'
						) }
						onCreate={ onCreateCategory }
					/>
				</div>

				<div>
					<div className="nb-flex nb-items-baseline nb-justify-between nb-mb-2">
						<label className="nb-text-sm nb-font-semibold nb-text-neutral-700">
							{ __( 'Tags', 'nemtly-booking' ) }
						</label>
						<span className="nb-text-xs nb-text-neutral-500">
							{ sprintf(
								/* translators: %d: number of selected tags */
								__( '%d selected', 'nemtly-booking' ),
								formData.tags.length
							) }
						</span>
					</div>
					{ availableTags.length > 0 && (
						<div className="nb-flex nb-flex-wrap nb-gap-2 nb-max-h-[7.5rem] nb-overflow-y-auto">
							{ availableTags.map( ( tag ) => (
								<TermChip
									key={ tag.id }
									term={ tag }
									selected={ formData.tags.includes(
										tag.id
									) }
									onToggle={ ( e ) => {
										const updated = e.target.checked
											? [ ...formData.tags, tag.id ]
											: formData.tags.filter(
													( id ) => id !== tag.id
											  );
										onFieldChange( 'tags', updated );
									} }
								/>
							) ) }
						</div>
					) }
					<TermCreator
						placeholder={ __(
							'Create a new tag…',
							'nemtly-booking'
						) }
						onCreate={ onCreateTag }
					/>
				</div>
			</div>
		</div>
	);
};

export default BasicInfoStep;
